// Array.prototype.with() is ES2023, above our supported-browser floor
// (es-check gate: es2022). Our own code can't reach it — tsconfig `lib` is
// es2022, so tsc rejects it — but a bundled dependency can, and the es-check
// gate can't reliably distinguish the array method from unrelated `.with()`
// methods (e.g. OpenTelemetry's context.with), so a waived gate wouldn't
// catch it either. This install-if-missing shim makes such a dependency safe
// on pre-ES2023 browsers.
//
// This is NOT a complete or fully spec-compliant polyfill:
//   - Array.prototype only — TypedArray.prototype.with is not installed
//   - index coercion is simplified (Math.trunc, NaN → 0) rather than the
//     spec's full ToIntegerOrInfinity
//   - no generic array-like receiver handling (ToObject/LengthOfArrayLike)
// If we ever need real ES2023 coverage, replace this with core-js.
export const arrayWithPolyfill = function (
  this: unknown[],
  index: number,
  value: unknown
) {
  const copy = [...this]
  const relative = Math.trunc(index) || 0
  const actual = relative < 0 ? copy.length + relative : relative
  if (actual < 0 || actual >= copy.length) {
    throw new RangeError(`Invalid index : ${index}`)
  }
  copy[actual] = value
  return copy
}

// The `in` guard (rather than a property access) keeps our own bundle free of
// `.with` member expressions that the es-check feature detector would flag.
if (!('with' in Array.prototype)) {
  console.warn(
    'Array.prototype.with is missing in this environment — installing a minimal polyfill'
  )
  // eslint-disable-next-line no-extend-native -- extending the prototype is the whole point of a polyfill
  Object.defineProperty(Array.prototype, 'with', {
    value: arrayWithPolyfill,
    writable: true,
    configurable: true,
    // matches the native method, so the shim never leaks into for..in loops
    enumerable: false,
  })
}
